// [목적] fetch wrapper — { data, meta, error } envelope unwrap + 4xx/5xx throw + 인증/보안 헤더 자동 첨부
// [이유] fe-be-wire-up ADR-T1/T2/T3 — single-flight refresh Promise 싱글톤, credentials:'include', CSRF 자동 첨부
//         fe-screens-skeleton ADR-3 — 모든 API 호출이 단일 wrapper 경유, ErrorState 폴백 균일화
//         LGO-FE-02(auth-logout-hardening Wave 3) — 'gangwon:logout' 이벤트에 reason payload 추가.
//           refresh 실패 시 'session_expired'. 401 응답 바디의 error.code 가 'auth.SESSION_REVOKED' 류면 'force_revoked'.
//           user_initiated 는 useAuth.ts logout() 이 디스패치(본 파일 미사용).
// [사이드 이팩트] same-origin 요청에만 anon 세션 헤더·credentials·CSRF 첨부 (외부 도메인 누설 차단).
//                 refreshPromise 모듈 스코프 싱글톤 — 동시 다중 401 이 발생해도 refresh 는 1회만 호출됨.
//                 403 + auth.CSRF_* 에러코드 시 GET /auth/csrf 1회 재호출 후 retry — 무한루프 방지를 위해 1회만.
//                 gangwon:logout 이벤트 detail.reason: 'session_expired' | 'force_revoked' | 'user_initiated'.
// [수정 시 고려사항] logout 이벤트는 'gangwon:logout' CustomEvent 로 디스패치 — AuthProvider 가 구독하여 처리.
//                   refresh endpoint path 는 '/auth/refresh' 고정 — BE SessionService 와 동기화 필요.
//                   CSRF 쿠키명 'gw_csrf' 는 BE AuthController 상수와 일치시킬 것.
//                   force_revoked 판정은 향후 BE 가 401 바디에 auth.SESSION_REVOKED 코드를 반환하면 자동 활성화됨.
//                   현재는 refresh 실패 시 항상 session_expired — force_revoked 신호는 SEC-BE-08(auth-security-hardening) 연계.

/** LGO-FE-02: 'gangwon:logout' 이벤트 detail 타입 */
export type LogoutReason = 'session_expired' | 'force_revoked' | 'user_initiated';

/** LGO-FE-02: gangwon:logout CustomEvent detail 타입 */
export interface LogoutEventDetail {
  reason: LogoutReason;
}

/**
 * LGO-FE-02: gangwon:logout CustomEvent 디스패치 헬퍼
 * [목적] reason payload 와 함께 logout 이벤트를 발행해 AuthProvider 가 사유별로 처리하게 함
 * [이유] 무음 전환 폐지 — session_expired/force_revoked/user_initiated 구분으로 사용자 안내 분기
 * [사이드 이팩트] AuthProvider handleLogout 이 detail.reason 을 읽어 returnTo+error 라우팅 처리
 * [수정 시 고려사항] SEC-BE-08 완료 후 force_revoked 판정 로직(아래 inferReason) 보강
 */
function dispatchLogout(reason: LogoutReason): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<LogoutEventDetail>('gangwon:logout', { detail: { reason } }),
    );
  }
}

/**
 * LGO-FE-02: 401 응답 바디의 error.code 로 force_revoked 여부 판정
 * [목적] BE 가 'auth.SESSION_REVOKED' 류 코드를 내려주면 force_revoked 로 구분, 아니면 session_expired 기본
 * [이유] token_version 무효화(SEC-BE-08), 탈퇴, 도난 감지 시 서버가 별도 코드 반환 예정
 * [사이드 이팩트] 현재 BE 미구현 — 실질적으로 항상 session_expired 반환
 * [수정 시 고려사항] SEC-BE-08 완료 시 해당 코드(auth.SESSION_REVOKED, auth.TOKEN_VERSION_INVALID 등) 추가
 */
function inferReason(errorCode: string): LogoutReason {
  const FORCE_REVOKED_CODES = new Set([
    'auth.SESSION_REVOKED',
    'auth.TOKEN_VERSION_INVALID',
    'auth.ACCOUNT_DELETED',
  ]);
  return FORCE_REVOKED_CODES.has(errorCode) ? 'force_revoked' : 'session_expired';
}

import { ensureAnonSessionId } from './anonSession.js';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(public override cause?: unknown) {
    super('Network request failed');
    this.name = 'NetworkError';
  }
}

interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
  error: { code: string; message: string; details?: unknown } | null;
}

// [목적] BE API 오리진 — 공유 URL(A1)·PDF 다운로드 링크(A2) 조립에도 재사용되는 단일 소스
// [이유] FlowLine A1/A2: 공유 링크(${API 오리진}/share/folios/:id)와 PDF 다운로드 링크가
//        fetchWithEnvelope 와 동일한 "API 오리진" 규칙(비어있으면 same-origin 상대경로)을 따라야 한다.
//        기존에는 모듈 스코프 상수라 다른 파일이 import 할 수 없어 중복 계산 위험이 있었다.
// [사이드 이팩트] 없음 — 기존 값 그대로 export 만 추가(계산 로직 불변).
// [수정 시 고려사항] staging/prod 오리진 분리 정책은 기존과 동일 — VITE_API_BASE_URL 미설정 시 빈 문자열(same-origin).
export const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/**
 * [목적] same-origin 판별 — apiBase 또는 window.origin 과 일치하는 URL 에만 민감 헤더 첨부
 * [이유] 외부 CDN / 서드파티 URL 에 인증 헤더 누설 방지
 * [사이드 이팩트] apiBase 가 빈 문자열이면 모든 상대경로 URL 이 same-origin 으로 판별됨
 * [수정 시 고려사항] staging/prod 에서 FE/BE 오리진이 다른 경우 apiBase 에 BE origin 을 정확히 지정해야 함
 *                   2026-09-18(O-4): folio-export.client.ts downloadFolioExport() 가 anon 세션 헤더
 *                   첨부 여부 판단에 재사용하도록 export 추가(로직 변경 없음).
 */
export function isSameOrigin(url: string): boolean {
  if (url.startsWith('/')) return true;
  if (!url.startsWith('http')) return true;
  if (typeof window === 'undefined') return false;
  try {
    const u = new URL(url);
    return (
      u.origin === window.location.origin ||
      (apiBase !== '' && u.origin === new URL(apiBase).origin)
    );
  } catch {
    return false;
  }
}

/**
 * [목적] document.cookie 에서 gw_csrf 쿠키 값 추출
 * [이유] ADR-T3 — mutation 전 CSRF 토큰을 X-CSRF-Token 헤더로 첨부
 * [사이드 이팩트] httpOnly 가 아닌 gw_csrf 쿠키가 없으면 빈 문자열 반환 → BE CsrfGuard 가 403 응답
 * [수정 시 고려사항] 쿠키명 'gw_csrf' 는 BE AuthController 상수 CSRF_COOKIE 와 반드시 동기화
 */
function extractCsrfCookie(): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(/(?:^|;\s*)gw_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

const MUTATION_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/**
 * [목적] 모듈 스코프 single-flight refresh Promise 싱글톤
 * [이유] ADR-T1 — 동시 다중 401 발생 시 refresh 가 중복 호출되면 BE 의 refresh token rotation 정책이 깨짐.
 *         진행 중인 refresh Promise 를 재사용해 1회만 호출하고 나머지는 대기 후 retry
 * [사이드 이팩트] refresh 성공/실패 후 반드시 null 로 초기화 (finally 보장)
 * [수정 시 고려사항] 탭 간 공유 불가 (module scope) — Service Worker 레벨 공유가 필요하면 BroadcastChannel 검토
 */
let refreshPromise: Promise<void> | null = null;

/**
 * [목적] POST /auth/refresh 를 1회만 호출하는 single-flight 함수
 * [이유] ADR-T1 R-4 race condition 방지
 * [사이드 이팩트] 실패 시 'gangwon:logout' CustomEvent(reason=session_expired) 디스패치 → AuthProvider 가 상태 초기화
 *                 LGO-FE-02: reason 구분 — refresh 실패는 원칙적으로 session_expired.
 *                   단 응답 바디에 force_revoked 코드가 있으면 해당 reason 사용(inferReason).
 * [수정 시 고려사항] refresh 엔드포인트는 SkipCsrf 이므로 CSRF 헤더 불필요
 *                   SEC-BE-08 완료 시 refresh 401 바디에 auth.SESSION_REVOKED 코드 반환 예정 → inferReason 으로 자동 분기
 */
async function doRefresh(): Promise<void> {
  const url = `${apiBase}/auth/refresh`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    // LGO-FE-02: reason 구분 — refresh 실패 바디의 error.code 로 force_revoked 판정 시도
    let reason: LogoutReason = 'session_expired';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      reason = inferReason(body.error?.code ?? '');
    } catch {
      // JSON 파싱 실패 시 session_expired 기본값 유지
    }
    dispatchLogout(reason);
    throw new ApiError(res.status, 'auth.REFRESH_FAILED', 'Refresh token expired or invalid');
  }
}

/**
 * [목적] 401 시 single-flight refresh 를 수행(진행 중이면 합류) — 실패 시 ApiError(401, auth.REFRESH_FAILED)
 * [이유] fetchWithEnvelope 와 envelope 를 쓰지 않는 raw fetch 클라이언트(user-export.client 등)가 같은 refresh
 *        싱글톤을 공유해야 rotation 정책(동시 refresh 금지)이 지켜진다(2026-09-17 메인 검수에서 export 다운로드가
 *        access token 만료 시 refresh 없이 401 로 끝나던 문제 발견 → 공용화).
 * [사이드 이팩트] POST /auth/refresh 1회, 실패 시 logout 이벤트 디스패치(doRefresh 내부).
 * [수정 시 고려사항] 호출자는 성공 후 원 요청을 정확히 1회만 재시도할 것(무한 재시도 금지).
 */
export async function refreshSessionOnce(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  try {
    await refreshPromise;
  } catch {
    // doRefresh 내부에서 이미 logout 이벤트 디스패치 + ApiError throw
    throw new ApiError(401, 'auth.REFRESH_FAILED', 'Session expired');
  }
}

/**
 * [목적] CSRF 토큰을 BE 에서 재발급 받아 쿠키를 갱신하는 함수
 * [이유] ADR-T3 — 403 + auth.CSRF_* 응답 시 gw_csrf 쿠키가 만료됐을 가능성. 1회 재발급 후 retry
 * [사이드 이팩트] GET /auth/csrf 는 JwtAuthGuard 필수 — 로그인 상태에서만 유효
 * [수정 시 고려사항] 재발급 실패 시 원 에러를 그대로 throw
 */
async function refetchCsrf(): Promise<void> {
  const url = `${apiBase}/auth/csrf`;
  await fetch(url, { credentials: 'include' });
}

/**
 * [목적] HTTP fetch wrapper — envelope unwrap, 401 single-flight refresh retry, 403 CSRF retry
 * [이유] 모든 API 호출의 단일 진입점으로 인증/보안 cross-cutting concern 중앙화
 * [사이드 이팩트] 401 응답 시 refresh 후 원 요청 1회 retry. 403+CSRF 에러 시 csrf 재발급 후 1회 retry.
 *                 재시도 후에도 실패하면 ApiError throw
 * [수정 시 고려사항] _isRetry 플래그로 무한 재귀 방지. refresh/csrf 엔드포인트 자체는 retry 루프 미진입.
 *                   204 응답은 undefined 반환 (void endpoint 대응)
 */
export async function fetchWithEnvelope<T>(
  path: string,
  init: RequestInit & { anonSessionId?: string } = {},
  _isRetry = false,
): Promise<T> {
  const url = path.startsWith('http') ? path : `${apiBase}${path}`;
  const headers = new Headers(init.headers);

  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  const method = (init.method ?? 'GET').toUpperCase();

  if (isSameOrigin(url)) {
    // ADR-T2: credentials:'include' 자동 첨부 (same-origin only)
    // anonSession 헤더
    const sessionId = init.anonSessionId ?? ensureAnonSessionId();
    if (sessionId) headers.set('x-anon-session-id', sessionId);

    // ADR-T3: mutation 메서드에 CSRF 토큰 자동 첨부
    if (MUTATION_METHODS.has(method)) {
      const csrfToken = extractCsrfCookie();
      if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
    }
  }

  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers,
      ...(isSameOrigin(url) ? { credentials: 'include' } : {}),
    });
  } catch (err) {
    throw new NetworkError(err);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  let body: Envelope<T>;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    throw new ApiError(res.status, 'INVALID_RESPONSE', `Non-JSON response (status=${res.status})`);
  }

  // 401 → single-flight refresh + retry
  if (res.status === 401 && !_isRetry) {
    await refreshSessionOnce();
    return fetchWithEnvelope<T>(path, init, true);
  }

  // 403 + CSRF 에러 → csrf 재발급 후 1회 retry
  const errorCode = body.error?.code ?? '';
  if (res.status === 403 && errorCode.startsWith('auth.CSRF') && !_isRetry) {
    await refetchCsrf().catch(() => {
      // csrf 재발급 실패는 무시하고 원 에러 throw
    });
    return fetchWithEnvelope<T>(path, init, true);
  }

  if (!res.ok || body.error) {
    // dot-namespaced 에러 코드 그대로 노출 (INFRA-02)
    const code = body.error?.code ?? `HTTP_${res.status}`;
    const message = body.error?.message ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, code, message, body.error?.details);
  }

  return body.data;
}

export type { Envelope };
